<template>
  <component :is="wrapperIs" v-bind="wrapperBind">
    <el-form
      ref="form"
      :model="form"
      :rules="{}"
      :label-width="embedded ? '128px' : '120px'"
      size="medium"
      :class="['customer-form', { 'customer-form--embedded': embedded, 'form-page-form': !embedded }]"
    >
      <el-form-item v-if="isEdit" :label="$t('customerFormPage.customerNo')">
        <el-input v-model="form.customerNo" disabled />
      </el-form-item>

      <template v-if="!embedded">
        <el-divider content-position="left">{{ $t('customerFormPage.sectionPrimaryContact') }}</el-divider>
      </template>

      <el-row :gutter="embedGutter">
        <el-col :span="embedColHalf">
          <el-form-item :label="$t('common.firstName')">
            <el-input v-model="form.firstName" maxlength="80" clearable />
          </el-form-item>
        </el-col>
        <el-col :span="embedColHalf">
          <el-form-item :label="$t('common.lastName')">
            <el-input v-model="form.lastName" maxlength="80" clearable />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="embedGutter">
        <el-col :span="embedColHalf">
          <el-form-item :label="$t('grid.email')">
            <el-input v-model="form.email" maxlength="120" clearable />
          </el-form-item>
        </el-col>
        <el-col :span="embedColHalf">
          <el-form-item :label="$t('grid.phone')">
            <el-input v-model="form.phone" maxlength="40" clearable />
          </el-form-item>
        </el-col>
      </el-row>

      <template v-if="!embedded">
        <el-divider content-position="left">{{ $t('customerFormPage.sectionAddress') }}</el-divider>
      </template>

      <el-form-item :label="$t('customerFormPage.addrLine1')">
        <el-input v-model="form.addressLine1" maxlength="255" clearable />
      </el-form-item>
      <el-form-item :label="$t('customerFormPage.addrLine2')">
        <el-input v-model="form.addressLine2" maxlength="255" clearable />
      </el-form-item>

      <el-row :gutter="embedGutter">
        <el-col :span="embedColHalf">
          <el-form-item :label="$t('customerFormPage.city')">
            <el-input v-model="form.cityName" maxlength="100" clearable />
          </el-form-item>
        </el-col>
        <el-col :span="embedColHalf">
          <el-form-item :label="$t('customerFormPage.state')">
            <el-input v-model="form.stateCode" maxlength="32" clearable />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="embedGutter">
        <el-col :span="embedColHalf">
          <el-form-item :label="$t('customerFormPage.zipLabel')">
            <el-input v-model="form.zipCode" maxlength="20" clearable />
          </el-form-item>
        </el-col>
        <el-col :span="embedColHalf">
          <el-form-item :label="$t('customerFormPage.country')">
            <el-input v-model="form.countryCode" maxlength="8" :placeholder="$t('customerFormPage.countryPh')" clearable />
          </el-form-item>
        </el-col>
      </el-row>

      <el-form-item v-if="isEdit" :label="$t('common.status')">
        <el-select
          v-model="form.statusCode"
          :placeholder="$t('common.pleaseSelect')"
          :style="embedded ? { width: '100%' } : { width: '100%', maxWidth: '320px' }"
        >
          <el-option v-for="o in statusOptions" :key="o.value" :label="o.label" :value="o.value" />
        </el-select>
      </el-form-item>

      <el-form-item v-if="canAssignSales" :label="$t('customerFormPage.salesOwner')">
        <el-select
          v-model="form.assignedSalesUserId"
          clearable
          filterable
          :placeholder="$t('customerFormPage.salesOwnerPh')"
          :style="embedded ? { width: '100%' } : { width: '100%', maxWidth: '320px' }"
        >
          <el-option v-for="s in salesOptions" :key="s.value" :label="s.label" :value="s.value" />
        </el-select>
      </el-form-item>

      <el-form-item v-if="!embedded" class="form-page-form__actions">
        <el-button type="primary" :loading="saving" @click="submit">{{ $t('common.save') }}</el-button>
        <el-button @click="$router.push('/customer/list')">{{ $t('common.backToList') }}</el-button>
      </el-form-item>
    </el-form>
  </component>
</template>

<script>
import { createCustomer, updateCustomer, getCustomer } from '@/api/customer'
import { getSalesUserList } from '@/api/user'
import { enumSelectOptions } from '@/utils/crmLabels'
import FormPageShell from '@/components/FormPageShell.vue'

export default {
  name: 'CustomerForm',
  components: { FormPageShell },
  props: {
    embedded: { type: Boolean, default: false },
    recordId: { type: [String, Number], default: null }
  },
  data() {
    return {
      isEdit: false,
      saving: false,
      form: {
        customerNo: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        addressLine1: '',
        addressLine2: '',
        cityName: '',
        stateCode: '',
        zipCode: '',
        countryCode: 'US',
        statusCode: 1,
        assignedSalesUserId: ''
      },
      salesOptions: []
    }
  },
  computed: {
    embedGutter() {
      return this.embedded ? 16 : 0
    },
    embedColHalf() {
      return this.embedded ? 12 : 24
    },
    wrapperIs() {
      return this.embedded ? 'div' : 'FormPageShell'
    },
    wrapperBind() {
      if (this.embedded) {
        return { class: 'customer-form__embed' }
      }
      return {
        isEdit: this.isEdit,
        title: this.isEdit ? this.$t('customerFormPage.editTitle') : this.$t('customerFormPage.newTitle'),
        subtitle: this.isEdit ? '' : this.$t('customerFormPage.newSubtitle'),
        icon: 'el-icon-office-building',
        theme: 'primary',
        wide: true
      }
    },
    statusOptions() {
      void this.$locale
      return enumSelectOptions(this.$t, 'customerStatus', [1, 2])
    },
    canAssignSales() {
      const roles = (this.$store.state.userInfo && this.$store.state.userInfo.roleCodes) || []
      return roles.includes('OFFICE') || roles.includes('ADMIN')
    }
  },
  async created() {
    if (this.canAssignSales) {
      this.loadSalesOptions()
    }
    if (this.embedded) {
      const rid = this.recordId
      this.isEdit = rid != null && rid !== ''
      if (this.isEdit) {
        try {
          await this.loadDetail(rid)
        } catch (e) {
          this.$emit('cancel')
        }
      } else {
        this.form.countryCode = 'US'
      }
      return
    }
    const id = this.$route.params.id
    if (id) {
      this.isEdit = true
      await this.loadDetail(id)
    } else {
      this.form.countryCode = 'US'
    }
  },
  methods: {
    editId() {
      if (this.embedded) return this.recordId
      return this.$route.params.id
    },
    async loadDetail(id) {
      try {
        const r = await getCustomer(id)
        const c = r.data !== undefined ? r.data : r
        if (!c || !c.id) {
          this.$message.error(this.$t('customerFormPage.notFound'))
          if (!this.embedded) this.$router.replace('/customer/list')
          throw new Error('not found')
        }
        this.form = {
          customerNo: c.customerNo || '',
          firstName: c.firstName || '',
          lastName: c.lastName || '',
          email: c.email || '',
          phone: c.phone || '',
          addressLine1: c.addressLine1 || '',
          addressLine2: c.addressLine2 || '',
          cityName: c.cityName || '',
          stateCode: c.stateCode || '',
          zipCode: c.zipCode || '',
          countryCode: c.countryCode || 'US',
          statusCode: c.statusCode != null ? Number(c.statusCode) : 1,
          assignedSalesUserId: c.assignedSalesUserId || ''
        }
      } catch (e) {
        if (e.message !== 'not found') {
          this.$message.error((e && e.message) || this.$t('common.loadFailed'))
          if (!this.embedded) this.$router.replace('/customer/list')
        }
        throw e
      }
    },
    buildPayload() {
      const payload = {
        firstName: this.form.firstName || '',
        lastName: this.form.lastName || '',
        email: this.form.email || '',
        phone: this.form.phone || '',
        addressLine1: this.form.addressLine1 || '',
        addressLine2: this.form.addressLine2 || '',
        cityName: this.form.cityName || '',
        stateCode: this.form.stateCode || '',
        zipCode: this.form.zipCode || '',
        countryCode: (this.form.countryCode || '').trim() || 'US',
        statusCode: this.isEdit ? Number(this.form.statusCode) : undefined
      }
      if (this.canAssignSales) {
        payload.assignedSalesUserId = this.form.assignedSalesUserId || null
      }
      return payload
    },
    async loadSalesOptions() {
      try {
        const r = await getSalesUserList()
        const rows = r.data || []
        this.salesOptions = rows.map(item => ({ value: item.id, label: item.displayName || item.username }))
      } catch (e) {
        this.salesOptions = []
      }
    },
    submit() {
      this.$refs.form.validate(async valid => {
        if (!valid) return
        this.saving = true
        this.$emit('saving', true)
        try {
          const payload = this.buildPayload()
          if (this.isEdit) {
            const id = this.editId()
            await updateCustomer(id, payload)
            this.$message.success(this.$t('common.saved'))
            if (this.embedded) this.$emit('saved', { id })
          } else {
            const r = await createCustomer(payload)
            const newId = r.data !== undefined ? r.data : r
            this.$message.success(this.$t('common.created'))
            if (this.embedded) {
              this.$emit('saved', { id: newId })
            } else if (newId) {
              this.$router.replace('/customer/edit/' + newId)
            } else {
              this.$router.push('/customer/list')
            }
          }
        } catch (e) {
          this.$message.error((e && e.message) || this.$t('common.failed'))
        } finally {
          this.saving = false
          this.$emit('saving', false)
        }
      })
    }
  }
}
</script>

<style scoped>
.form-page-form {
  max-width: 720px;
}

.customer-form--embedded {
  max-width: none;
}

.customer-form__embed {
  min-width: 0;
}

.form-page-form__actions {
  margin-top: 8px;
  margin-bottom: 0;
}
</style>
